import React, { useEffect, useState } from "react";
import { useQuery, useMutation } from "react-query";
import MaterialReactTable from "material-react-table";
import {
  Spinner,
  Toast,
  ToastBody,
  ToastHeader,
  Modal,
  ModalHeader,
  ModalBody,
  Table,
  Button
} from "reactstrap";
import useAxios from "../../sections/useAxios";
import { toast } from "react-toastify";
import InvestorEditForm from "./components/investor-edit-form-modal";
import ConfirmDialog from "../../components/common/confirmation-dialog.jsx";
import { CircularProgress } from "@mui/material";

const InvestorsListing = () => {
  const api = useAxios();
  const [showToast, setShowToast] = useState(false);
  const [toastMsg, setToastMsg] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteType, setDeleteType] = useState("investor"); // "investor" or "registration"

  const [permissionModalOpen, setPermissionModalOpen] = useState(false);
  const [permissionAction, setPermissionAction] = useState(null); // "allow" | "block"

  const {
    data,
    isLoading: investorsLoading,
    isError,
    error,
    refetch,
  } = useQuery(
    ["investors-list"],
    async () => {
      const res = await api.get(`company/all-available-investors`);
      return res.data;
    },
    { cacheTime: 0 }
  );

  const investors = data ?? [];


  const {
    data: blockCountData,
    isLoading: blockCountLoading,
    refetch: blockCountRefetch,
  } = useQuery(
    ["block-stats"],
    async () => {
      const res = await api.get(`investor/block-stats`);
      return res.data;
    },
    { cacheTime: 0 }
  );

  const blockStats = blockCountData ?? [];

  // Delete Investor / Registration Mutation
  const deleteMutation = useMutation(
    async ({ id, type }) => {
      if (type === "investor") {
        return api.delete(`company/update-investor/${id}`);
      }
      return api.delete(`company/admin-cancel-registration/${id}`);
    },
    {
      onSuccess: (_, variables) => {
        if (variables.type === "investor") {
          toast.success("Investor deleted successfully");
          refetch();
        } else {
          toast.success("Registration deleted successfully");
          // remove from modal state
          setSelectedInvestor((prev) => ({
            ...prev,
            registrations: prev.registrations.filter(
              (r) => r.id !== variables.id
            ),
          }));
        }
        setConfirmDialogOpen(false);
      },
      onError: () => {
        toast.error("Error deleting record");
        setConfirmDialogOpen(false);
      },
    }
  );

  const updateMutation = useMutation(
    async (values) => {
      return api.post(`/investor/block-toggle/${values.id}`, values);
    },
    {
      onSuccess: () => {
        toast.success(error?.response?.data?.message);
        refetch();
        blockCountRefetch();
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message);
      },
    }
  );

  useEffect(() => {
    if (isError) {
      setToastMsg(error.message || "Something went wrong");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 4000);
    }
  }, [isError, error]);

  const columns = [
    { accessorKey: "id", header: "ID", size: 50 },
    {
      accessorKey: "name",
      header: "Investor Name",
    },
    { accessorKey: "email", header: "Email" },
    { accessorKey: "company", header: "Company" },
    {
      accessorKey: "registration_count",
      header: "Registration Count",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
    },
    {
      accessorKey: "registrations",
      header: "Registrations",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ row }) => (
        <i
          className="fa-solid fa-clipboard-list fa-xl text-primary"
          style={{ cursor: "pointer" }}
          onClick={() => {
            setSelectedInvestor(row.original);
            setModalOpen(true);
          }}
        ></i>
      ),
    },
    {
      accessorKey: "created_at",
      header: "Created At",
      Cell: ({ cell }) => new Date(cell.getValue()).toLocaleDateString(),
    },
    {
      accessorKey: "is_blocked", header: "Status",

      Cell: ({ row }) => (
        <span
        >
          {row.original.is_blocked ?

            <span>Blocked&nbsp;<i class="fa-solid fa-ban me-2 text-center text-danger" style={{ width: "20px" }}></i></span>
            :

            <span>Open&nbsp;<i class="fa-solid fa-user-check me-2 text-center text-success" style={{ width: "20px" }}></i></span>

          }
        </span>
      ),

    },
    {
      header: "Block/Unblock",
      Cell: ({ row }) => {
        const isRowLoading =
          updateMutation.isLoading &&
          updateMutation.variables?.id === row.original.id;

        return (
          <span
            role="button"
            className="text-primary"
            style={{ cursor: "pointer" }}
            onClick={() =>
              updateMutation.mutate({
                id: row.original.id,
                is_blocked: !row?.original?.is_blocked,
              })
            }
          >
            {isRowLoading ? (
              <span>Processing...</span>
            ) : row?.original?.is_blocked ? (
              <i className="fa-solid fa-user-check me-2 text-center text-success" style={{ width: "20px" }}></i>
            ) : (
              <i className="fa-solid fa-ban me-2 text-center text-danger" style={{ width: "20px" }}></i>
            )}
          </span>
        );
      },
    },
    {
      accessorKey: "actions",
      header: "Actions",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ row }) => (
        <div className="d-flex gap-3 justify-content-center">
          <i
            className="fa-solid fa-pen text-success pointer"
            onClick={() => {
              setSelectedInvestor(row.original);
              setEditModalOpen(true);
            }}
          ></i>
          <i
            className="fa-solid fa-trash text-danger pointer"
            onClick={() => {
              setDeleteId(row.original.id);
              setDeleteType("investor");
              setConfirmDialogOpen(true);
            }}
          ></i>
        </div>
      ),
    },
  ];

  const permissionMutation = useMutation(
    async ({ action, reason }) => {
      const endpoint =
        action === "allow"
          ? "investor/unblock-all"
          : "investor/block-all";

      return api.post(endpoint, {
        reason,
      });
    },
    {
      onSuccess: () => {
        toast.success(
          permissionAction === "allow"
            ? "All investors are now allowed to book meetings."
            : "All investors are now blocked from booking meetings."
        );

        blockCountRefetch();

        setPermissionModalOpen(false);
        setPermissionAction(null);

        refetch();
      },
      onError: (err) => {
        toast.error(
          err?.response?.data?.message ||
          "Failed to update investor booking permissions."
        );
      },
    }
  );


  const investorPermissionMutation = useMutation(
    async ({ id, action }) => {
      const endpoint =
        action === "allow"
          ? `company/allow-investor-booking/${id}`
          : `company/block-investor-booking/${id}`;

      return api.post(endpoint);
    },
    {
      onSuccess: (_, variables) => {
        toast.success(
          variables.action === "allow"
            ? "Investor is now allowed to book meetings."
            : "Investor is now blocked from booking meetings."
        );

        refetch();
      },
      onError: (err) => {
        toast.error(
          err?.response?.data?.message ||
          "Failed to update investor booking permission."
        );
      },
    }
  );

  if (investorsLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center my-4" style={{ height: "50vh" }}>
        <Spinner color="primary" />
      </div>
    )
  }

  return (
    <div className="container-fluid mt-4">
      <h4 className="primary-color fw-bold">Investor's Listing</h4>

      {showToast && (
        <div
          style={{
            position: "fixed",
            top: 20,
            right: 20,
            zIndex: 1050,
            minWidth: "250px",
          }}
        >
          <Toast isOpen={showToast}>
            <ToastHeader icon="danger">Error</ToastHeader>
            <ToastBody>{toastMsg}</ToastBody>
          </Toast>
        </div>
      )}

      <div className="d-flex justify-content-between mb-2">

        <span>
          <p>
            Blocked Investors:{" "}
            {blockCountLoading ? (
              <CircularProgress size={18} />
            ) : (
              `${blockStats.blocked || 0}/${blockStats.total || 0}`
            )}
          </p>
        </span>

        <span>

          <Button
            className="primary-bg text-white secondary-hover me-3"
            onClick={() => {
              setPermissionAction("allow");
              setPermissionModalOpen(true);
            }}
          >
            Allow all investors to book meeting
          </Button>

          <Button
            className="danger-bg text-white"
            onClick={() => {
              setPermissionAction("block");
              setPermissionModalOpen(true);
            }}
          >
            Block all investors from booking meeting
          </Button>

        </span>
      </div>

      {!investorsLoading && !isError && (
        <MaterialReactTable
          columns={columns}
          data={investors}
          muiTablePaperProps={{
            elevation: 10,
          }}
        />
      )}

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)} size="lg">
        <ModalHeader toggle={() => setModalOpen(!modalOpen)}>
          Registrations for {selectedInvestor?.name}
        </ModalHeader>
        <ModalBody>
          {selectedInvestor?.registrations?.length > 0 ? (
            <Table bordered hover responsive>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Meeting</th>
                  <th>Company</th>
                  <th>Location</th>
                  <th>Start Time</th>
                  <th>End Time</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {selectedInvestor.registrations.map((reg) => (
                  <tr key={reg.id}>
                    <td>{reg.id}</td>
                    <td>{reg.meeting}</td>
                    <td>{reg.company}</td>
                    <td>{reg.location}</td>
                    <td>{reg.start_time}</td>
                    <td>{reg.end_time}</td>
                    <td className="text-center">
                      <i
                        className="fa-solid fa-trash text-danger pointer"
                        onClick={() => {
                          setDeleteId(reg.id);
                          setDeleteType("registration");
                          setConfirmDialogOpen(true);
                        }}
                      ></i>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <p>No registrations found for this investor.</p>
          )}
        </ModalBody>
      </Modal>

      <InvestorEditForm
        isOpen={editModalOpen}
        toggle={() => setEditModalOpen(!editModalOpen)}
        investor={selectedInvestor}
        refetch={refetch}
      />

      <ConfirmDialog
        isOpen={confirmDialogOpen}
        toggle={() => setConfirmDialogOpen(!confirmDialogOpen)}
        title="Confirm Delete"
        message={`Are you sure you want to delete this ${deleteType === "investor" ? "investor" : "registration"
          }?`}
        onConfirm={() => deleteMutation.mutate({ id: deleteId, type: deleteType })}
        loading={deleteMutation.isLoading}
      />



      <Modal
        isOpen={permissionModalOpen}
        toggle={() => setPermissionModalOpen(!permissionModalOpen)}
        centered
      >
        <ModalHeader toggle={() => setPermissionModalOpen(!permissionModalOpen)}>
          {permissionAction === "allow"
            ? "Allow Investor Booking"
            : "Block Investor Booking"}
        </ModalHeader>

        <ModalBody>
          {permissionAction === "allow" ? (
            <>
              <div className="alert alert-success">
                <strong>Allow all investors to book meetings?</strong>
                <br />
                This will allow all investors in the system to book meetings.
              </div>
            </>
          ) : (
            <>
              <div className="alert alert-danger">
                <strong>Block all investors from booking meetings?</strong>
                <br />
                This will prevent all investors from booking new meetings.
              </div>
            </>
          )}



          <div className="d-flex justify-content-end gap-2">
            <Button
              color="secondary"
              outline
              onClick={() => {
                setPermissionModalOpen(false);
                setPermissionAction(null);
              }}
              disabled={permissionMutation.isLoading}
            >
              Cancel
            </Button>

            <Button
              color={permissionAction === "allow" ? "success" : "danger"}
              onClick={() =>
                permissionMutation.mutate({
                  action: permissionAction,
                })
              }
              disabled={permissionMutation.isLoading}
            >
              {permissionMutation.isLoading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Processing...
                </>
              ) : permissionAction === "allow" ? (
                "Allow All Investors"
              ) : (
                "Block All Investors"
              )}
            </Button>
          </div>
        </ModalBody>
      </Modal>


    </div>
  );
};

export default InvestorsListing;
